import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Icon3D from '../components/Icon3D';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { getDiagnosticProgress, getDiagnosticResults } from '../api/endpoints';
import {
  CheckCircle, Brain, TrendingUp, ChevronRight, ArrowRight, Zap, Star
} from 'lucide-react';

const DiagnosticResultsPage = () => {
  const { tenant } = useTenant();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = `Diagnostic Results | ${tenant?.name || 'MedMCQ'}`;
  }, [tenant]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [progRes, resRes] = await Promise.all([
        getDiagnosticProgress(),
        getDiagnosticResults(),
      ]);
      setProgress(progRes.data);
      setResults(resRes.data);
    } catch (e) {
      console.error('Failed to load results:', e);
    } finally {
      setLoading(false);
    }
  };

  const getStrengthLabel = (pct) => {
    if (pct >= 80) return { label: 'Strong', color: 'text-emerald-600', bg: 'bg-emerald-100' };
    if (pct >= 60) return { label: 'Good', color: 'text-cyan-600', bg: 'bg-cyan-100' };
    if (pct >= 40) return { label: 'Developing', color: 'text-amber-600', bg: 'bg-amber-100' };
    return { label: 'Needs Focus', color: 'text-red-600', bg: 'bg-red-100' };
  };

  const getBarColor = (pct) => {
    if (pct >= 80) return 'linear-gradient(90deg, #10b981, #059669)';
    if (pct >= 60) return 'linear-gradient(90deg, #06b6d4, #0891b2)';
    if (pct >= 40) return 'linear-gradient(90deg, #f59e0b, #d97706)';
    return 'linear-gradient(90deg, #ef4444, #dc2626)';
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-12 flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const categories = results?.category_results || [];
  const overallPct = results?.overall_accuracy_pct ?? null;
  const totalAnswered = results?.total_answered ?? 0;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-10">

        {/* Celebration header */}
        <div
          className="rounded-2xl p-8 text-white mb-8 relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #0a192f 0%, #164e63 100%)' }}
        >
          <div className="absolute top-0 right-0 w-48 h-48 opacity-10">
            <Star className="w-full h-full text-cyan-300" />
          </div>
          <div className="relative z-10 flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-4 h-4 text-cyan-400" />
                <span className="text-cyan-400 text-sm font-semibold uppercase tracking-wide">
                  Diagnostic Complete
                </span>
              </div>
              <h1 className="text-2xl font-bold mb-2">
                Your personalised learning engine is now active
              </h1>
              <p className="text-slate-300 text-sm leading-relaxed">
                Excellent work completing all 3 diagnostic tests. Every question you practise from
                now on is chosen specifically to target your weak areas and reinforce your strengths.
                Your adaptive profile has been built — this is where real improvement begins.
              </p>
              {overallPct !== null && (
                <div className="mt-4 flex items-center gap-6">
                  <div>
                    <div className="text-3xl font-bold text-cyan-300">{overallPct}%</div>
                    <div className="text-xs text-slate-400">Overall accuracy</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-cyan-300">{totalAnswered}</div>
                    <div className="text-xs text-slate-400">Questions completed</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Category breakdown */}
        {categories.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">Category Performance</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Your adaptive engine will prioritise lower-scoring areas in your practice sessions.
              </p>
            </div>
            <div className="p-6 space-y-5">
              {categories.map((cat, i) => {
                const strength = getStrengthLabel(cat.accuracy_pct);
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-800 capitalize">
                          {cat.category}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${strength.bg} ${strength.color}`}>
                          {strength.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-slate-400 text-xs">{cat.correct}/{cat.total}</span>
                        <span className="font-bold text-slate-800 w-10 text-right">{cat.accuracy_pct}%</span>
                      </div>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${cat.accuracy_pct}%`,
                          background: getBarColor(cat.accuracy_pct),
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Adaptive engine explainer */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-slate-200 p-5" style={{ boxShadow: 'var(--shadow-card)' }}>
            <div className="mb-3">
              <Icon3D icon={Brain} colour="turquoise" size="sm" />
            </div>
            <h3 className="font-bold text-slate-800 text-sm mb-1">Personalised Questions</h3>
            <p className="text-xs text-slate-500">
              Every question you see is now selected based on your specific knowledge profile.
            </p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5" style={{ boxShadow: 'var(--shadow-card)' }}>
            <div className="mb-3">
              <Icon3D icon={TrendingUp} colour="green" size="sm" />
            </div>
            <h3 className="font-bold text-slate-800 text-sm mb-1">Targeted Improvement</h3>
            <p className="text-xs text-slate-500">
              Weaker categories receive more questions, matched to the right difficulty level.
            </p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5" style={{ boxShadow: 'var(--shadow-card)' }}>
            <div className="mb-3">
              <Icon3D icon={Zap} colour="turquoise" size="sm" />
            </div>
            <h3 className="font-bold text-slate-800 text-sm mb-1">Adaptive Difficulty</h3>
            <p className="text-xs text-slate-500">
              Questions get harder as you improve, keeping you in the optimal learning zone.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => navigate('/questions')}
            className="flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl text-white font-bold text-base transition-all hover:opacity-90 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, rgb(6, 182, 212) 0%, rgb(8, 145, 178) 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full" style={{ animation: 'shimmer 3s infinite' }} />
            <span className="relative z-10 flex items-center gap-2">
              Start Personalised Practice
              <ArrowRight className="w-5 h-5" />
            </span>
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl text-slate-600 font-semibold text-base border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all"
          >
            Go to Dashboard
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

      </div>
    </Layout>
  );
};

export default DiagnosticResultsPage;
